import { chain, compact, findIndex, includes, set, toLower } from "lodash"
import { upsellingTitle } from "helpers/op-pro"
import { ResourceType } from "./resource-types"
import { Role } from "model/state"

type TabType = "members" | "teams" | "tokens" | "personal-tokens" | "applications" | "report" | "permissions" | "entitlements"

interface TabTemplate {
  index: number
  id: TabType
  label: string
  isDisabled: (params: {
    isOpPro: boolean
    resourceType: ResourceType
  }) => boolean
}

const DEFAULT_TABS: TabTemplate[] = [
  {
    index: 0,
    id: "members",
    label: "Members",
    isDisabled: ({ isOpPro }) => isOpPro,
  },
  {
    index: 1,
    id: "teams",
    label: "Teams",
    isDisabled: ({ isOpPro }) => isOpPro,
  },
  {
    index: 2,
    id: "tokens",
    label: "Tokens",
    isDisabled: ({ isOpPro, resourceType }) => isOpPro || resourceType === "TEMPLATE",
  },
  {
    index: 3,
    id: "applications",
    label: "Applications",
    isDisabled: ({ resourceType }) => !includes(["PORTFOLIO", "SWAP"], resourceType),
  },
]

interface PermissionTab {
  id: TabType
  label: string
  align?: "right"
  disabled?: boolean
  // [TODO:TS] Could be refined
  tooltip?: string | null
}

export function computeDatasetPermissionsTabs(route: string, path: TabType) {
  const tabs: PermissionTab[] = [
    { id: "members", label: "Members" },
    { id: "teams", label: "Teams" },
    { id: "tokens", label: "Tokens" },
    { id: "report", label: "Access report", align: "right" },
  ]
  return computeTabs(tabs, route, path)
}

export function computeDatasetEntitlementsTabs(route: string, path: TabType, isOpPro: boolean) {
  const tabs: PermissionTab[] = [
    { id: "permissions", label: "Permissions" },
    { id: "entitlements", label: "Dataset Entitlements", disabled: isOpPro },
  ]
  return computeTabs(tabs, route, path)
}

export function computeEntityPermissionsTabs(route: string, path: TabType, isOpPro: boolean, role: Role, isPatFeatureEnabled: boolean) {
  const tabs: PermissionTab[] = compact([
    { id: "members", label: "Members" },
    { id: "teams", label: "Teams", disabled: isOpPro },
    { id: "tokens", label: "Tokens", disabled: isOpPro },
    // Absent entirely for a non-admin or when the flag is off, never disabled: a disabled tab
    // would tell a non-admin the surface exists.
    role === "admin" && isPatFeatureEnabled && { id: "personal-tokens", label: "Personal Tokens" },
  ])
  return computeTabs(tabs, route, path)
}

function getTabTooltip(isOpPro: boolean, resourceType: ResourceType, tab: PermissionTab) {
  // OpPro will always get the upsell tooltip
  const upsellTooltip = upsellingTitle(isOpPro, resourceType)
  if (upsellTooltip) {
    return upsellTooltip
  }

  if (tab.id === "applications") {
    return "Applications can only be given permissions to portfolios and baskets"
  }

  if (toLower(resourceType) === "template" && tab.id === "tokens") {
    return "Templates can only be given permissions to teams and members"
  }

  return null
}

function processTab(isOpPro: boolean, resourceType: ResourceType, tab: TabTemplate): PermissionTab {
  if (!tab.isDisabled || !tab.isDisabled({ isOpPro, resourceType })) {
    return {
      ...tab,
      disabled: false,
    }
  }

  return {
    ...tab,
    disabled: true,
    tooltip: getTabTooltip(isOpPro, resourceType, tab),
  }
}

export function computeResourcePermissionsTabs(route: string, path: TabType, resourceType: ResourceType, isOpPro: boolean) {
  const tabs = chain(DEFAULT_TABS)
    .map(tab => processTab(isOpPro, resourceType, tab))
    .orderBy(["disabled", "index"], ["asc", "asc"])
    .value()

  return computeTabs(tabs, route, path)
}

export function computeTeamTabs(route: string, path: TabType, datasetEntitlementsEnabled: boolean, role: Role, teamRole: Role, isAllAccessTeam: boolean) {
  const hasReadAccess = role === "admin" || teamRole

  const tabs: PermissionTab[] = compact([
    { id: "members", label: "Members" },
    { id: "tokens", label: "Tokens" },
    // Only team members or admins should see permissions tab.
    !isAllAccessTeam && hasReadAccess && { id: "permissions", label: "Permissions" },
    !isAllAccessTeam && datasetEntitlementsEnabled && hasReadAccess && { id: "entitlements", label: "Dataset Entitlements" },
  ])

  return computeTabs(tabs, route, path)
}

// [TODO:TS] This type could be more restrictive, as URL can only be defined if disabled is false
interface ComputedPermissionTab extends PermissionTab {
  url?: string
  selected?: boolean
}

function computeTabs(tabs: PermissionTab[], route: string, path: TabType): ComputedPermissionTab[] {
  return chain(tabs)
    .compact()
    .map((tab): ComputedPermissionTab => tab.disabled === true ? tab : {
      ...tab,
      disabled: false,
      url: `${route}${tab.id}`,
    })
    .thru((tabs) => {
      const selectedIndex = findIndex(tabs, ({ id }) => id === path)
      if (selectedIndex === -1) {
        return tabs
      }
      set(tabs, [selectedIndex, "selected"], true)
      return tabs
    })
    .value()
}
